import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import { apiManagementI18n } from './i18n';

export default function ApiManagement() {
  // 1) Hooks first
  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Card, Button, Table, Badge, IconButton } =
    DesignSystemComponents ?? {};

  const { language } = useLanguage();
  const apiLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: apiManagementI18n });

  useEffect(() => {
    apiManagementI18n.changeLanguage(apiLanguage);
  }, [apiLanguage]);

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    import('./dummyApisTable')
      .then((m) => {
        if (!mounted) return;
        const data = m.dummyApisTable || [];
        setRows(data);
        setTotalItems(data.length);
      })
      .catch((error) => {
        console.error('Error loading API table data:', error);
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(0);
  };

  const onCreate = () => {
    // TODO: open modal / navigate to form
  };
  const onEdit = (row) => {
    // TODO: open edit modal / navigate with row.id
  };
  const onDelete = (row) => {
    // TODO: confirm + delete
  };

  if (!Card || !Button || !Table || !Badge) return null;

  return (
    <Card specialPadding='p-4'>
      {/* Header */}
      <div className='d-flex justify-content-between mb-3'>
        <div>
          <h2 className='mb-1'>{t('api_pageTitle')}</h2>
          <p className='caption font-light'>
            {t('api_pageDescription')}
          </p>
        </div>
        <Button
          label={t('api_addBtn')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={onCreate}
        />
      </div>

      {/* Table */}
      <div className='table-container'>
        <Table
          columns={[
            { accessor: 'name', label: t('api_col_name') },
            {
              accessor: 'url',
              label: t('api_col_url'),
              render: (row) => (
                <a
                  href={row.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-decoration-none text-primary'
                >
                  {row.url}
                </a>
              ),
            },
            {
              accessor: 'method',
              label: t('api_col_method'),
              render: (row) => (
                <Badge
                  text={row.method}
                  color={row.method === 'GET' ? 'info' : 'primary'}
                  size='sm'
                />
              ),
            },
            { accessor: 'description', label: t('api_col_description') },
            {
              label: t('api_col_actions'),
              render: (row) => (
                <div className='d-flex gap-3'>
                  <IconButton
                    ariaLabel={t('api_editAria', { name: row.name })}
                    icon='pencil'
                    onClick={() => onEdit(row)}
                    size='md'
                    iconStroke='var(--primary-500)'
                  />
                  <IconButton
                    ariaLabel={t('api_deleteAria', { name: row.name })}
                    icon='trash'
                    onClick={() => onDelete(row)}
                    size='md'
                    iconStroke='var(--error-500)'
                  />
                </div>
              ),
            },
          ]}
          data={rows}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          totalItems={totalItems}
          paginator
          paginatorGroup={[
            { label: '10', value: 10 },
            { label: '20', value: 20 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
          ]}
          loading={loading}
        />
      </div>
    </Card>
  );
}
